import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Clock, MapPin, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import {
  type CarrierDispatchCenter,
  type ServingCell,
  useCarrierDispatchCenters,
  useCarrierServing,
  useUpdateServingDefaultDropoff,
} from "@/api/carrier";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { NativeSelect } from "@/components/ui/native-select";
import { Skeleton } from "@/components/ui/skeleton";
import { DeliveryScheduleSheet } from "@/features/delivery-schedule-sheet";
import { hasHttpStatus } from "@/lib/api-error";

export default function DeliverySetupScreen() {
  const [editingCenter, setEditingCenter] =
    useState<CarrierDispatchCenter | null>(null);

  return (
    <div className="carrier-page">
      <PageHeader title="Delivery setup" />

      <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        These settings drive the delivery estimates customers are shown.
      </p>
      <DispatchCentersSection onEdit={setEditingCenter} />
      <ServingSection />

      {editingCenter && (
        <DeliveryScheduleSheet
          center={editingCenter}
          onClose={() => setEditingCenter(null)}
        />
      )}
    </div>
  );
}

function DispatchCentersSection({
  onEdit,
}: {
  onEdit: (center: CarrierDispatchCenter) => void;
}) {
  const centers = useCarrierDispatchCenters();
  const rows = centers.data ?? [];
  const forbidden = hasHttpStatus(centers.error, 403);

  return (
    <section aria-labelledby="trip-schedule-heading" className="space-y-3">
      <SectionHeading
        id="trip-schedule-heading"
        icon={<Clock className="h-4 w-4" />}
        title="Trip schedules"
        description="Processing time and recurring dispatch runs determine when pickup packages can move onward."
      />

      {centers.error && rows.length > 0 && (
        <CachedDataWarning
          message="Schedule refresh failed. Loaded dispatch-center settings remain available."
          onRetry={() => void centers.refetch()}
        />
      )}

      {centers.isLoading && rows.length === 0 ? (
        <SectionLoading label="Loading trip schedules" />
      ) : centers.error && rows.length === 0 ? (
        <SectionError
          title={
            forbidden
              ? "Trip schedules unavailable"
              : "Couldn’t load trip schedules"
          }
          message={
            forbidden
              ? "You do not have permission to view delivery setup."
              : "Check your connection and try again."
          }
          onRetry={forbidden ? undefined : () => void centers.refetch()}
        />
      ) : rows.length === 0 ? (
        <EmptySection
          title="No dispatch centers yet"
          message="Dispatch centers appear here once your carrier has an operating location."
        />
      ) : (
        <div className="grid gap-2 lg:grid-cols-2">
          {rows.map((center) => (
            <ScheduleCard
              key={center.id}
              center={center}
              onEdit={() => onEdit(center)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ScheduleCard({
  center,
  onEdit,
}: {
  center: CarrierDispatchCenter;
  onEdit: () => void;
}) {
  const runCount = center.runSchedule
    ? Object.values(center.runSchedule.weekly).reduce(
        (total, runs) => total + runs.length,
        0
      )
    : 0;
  const activeDays = center.runSchedule
    ? Object.values(center.runSchedule.weekly).filter((runs) => runs.length > 0)
        .length
    : 0;
  const overrideCount = center.runSchedule
    ? Object.keys(center.runSchedule.overrides).length
    : 0;
  const noRunOverrideCount = center.runSchedule
    ? Object.values(center.runSchedule.overrides).filter(
        (runs) => runs.length === 0
      ).length
    : 0;
  const replacementRunCount = center.runSchedule
    ? Object.values(center.runSchedule.overrides).reduce(
        (total, runs) => total + runs.length,
        0
      )
    : 0;
  const scheduleStatus =
    runCount > 0
      ? { code: "ESTIMATES_ON", label: "Estimates on" }
      : replacementRunCount > 0
        ? { code: "DATE_SPECIFIC_RUNS", label: "Date-specific runs" }
        : { code: "ESTIMATES_OFF", label: "Estimates off" };

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0 p-3">
        <div className="min-w-0">
          <CardTitle className="truncate text-sm">{center.name}</CardTitle>
          <CardDescription className="mt-1 text-xs">
            {center.code}
            {center.address?.city ? ` · ${center.address.city}` : ""} ·{" "}
            {center.timeZone}
          </CardDescription>
        </div>
        <StatusBadge
          status={scheduleStatus.code}
          label={scheduleStatus.label}
        />
      </CardHeader>
      <CardContent className="grid gap-3 border-t p-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end">
        <SummaryValue
          label="Processing"
          value={`${center.processingMinutes} min`}
        />
        <SummaryValue
          label="Weekly runs"
          value={`${runCount} across ${activeDays} days`}
        />
        <SummaryValue
          label="Date overrides"
          value={
            overrideCount === 0
              ? "0"
              : `${overrideCount} · ${noRunOverrideCount} no-run`
          }
        />
        <Button
          variant="outline"
          size="touch"
          className="w-full sm:w-auto"
          onClick={onEdit}
        >
          Edit schedule
        </Button>
      </CardContent>
    </Card>
  );
}

function SummaryValue({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm font-medium tabular-nums">{value}</div>
    </div>
  );
}

function ServingSection() {
  const serving = useCarrierServing();
  const rows = serving.data ?? [];
  const forbidden = hasHttpStatus(serving.error, 403);
  const groups = useMemo(() => groupServingCells(rows), [rows]);

  return (
    <section aria-labelledby="serving-heading" className="space-y-3">
      <SectionHeading
        id="serving-heading"
        icon={<MapPin className="h-4 w-4" />}
        title="Carrier handoff defaults"
        description="Choose where drivers hand off packages for each label carrier served by each dispatch center."
      />

      {serving.error && rows.length > 0 && (
        <CachedDataWarning
          message="Handoff refresh failed. Loaded default locations remain available."
          onRetry={() => void serving.refetch()}
        />
      )}

      {serving.isLoading && rows.length === 0 ? (
        <SectionLoading label="Loading carrier handoff defaults" />
      ) : serving.error && rows.length === 0 ? (
        <SectionError
          title={
            forbidden
              ? "Handoff defaults unavailable"
              : "Couldn’t load handoff defaults"
          }
          message={
            forbidden
              ? "You do not have permission to view carrier handoff defaults."
              : "Check your connection and try again."
          }
          onRetry={forbidden ? undefined : () => void serving.refetch()}
        />
      ) : rows.length === 0 ? (
        <EmptySection
          title="No label carriers granted yet"
          message="EZX manages which label carriers your carrier may serve."
        />
      ) : (
        <div className="space-y-3">
          {groups.map(([dispatchCenterId, group]) => (
            <Card key={dispatchCenterId}>
              <CardHeader className="border-b p-3">
                <CardTitle className="text-sm">{group.name}</CardTitle>
                <CardDescription className="text-xs">
                  {group.code} · {group.cells.length}{" "}
                  {group.cells.length === 1
                    ? "label carrier"
                    : "label carriers"}
                </CardDescription>
              </CardHeader>
              <CardContent className="divide-y p-0">
                {group.cells.map((cell) => (
                  <ServingRow
                    key={cell.labelCarrier.id}
                    dispatchCenterId={dispatchCenterId}
                    cell={cell}
                  />
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}

function ServingRow({
  dispatchCenterId,
  cell,
}: {
  dispatchCenterId: string;
  cell: ServingCell;
}) {
  const update = useUpdateServingDefaultDropoff();
  const [selected, setSelected] = useState(cell.defaultDropoffLocationId ?? "");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mutationLock = useRef(false);

  useEffect(() => {
    if (!mutationLock.current) {
      setSelected(cell.defaultDropoffLocationId ?? "");
    }
  }, [cell.defaultDropoffLocationId]);

  const handleChange = async (nextValue: string) => {
    if (mutationLock.current || nextValue === selected) return;
    const previousValue = selected;
    mutationLock.current = true;
    setSelected(nextValue);
    setPending(true);
    setError(null);
    try {
      await update.mutateAsync({
        dispatchCenterId,
        labelCarrierId: cell.labelCarrier.id,
        carrierDropoffLocationId: nextValue || null,
      });
      toast.success(`Default handoff saved for ${cell.labelCarrier.name}.`);
    } catch (caught) {
      const message = hasHttpStatus(caught, 403)
        ? "You do not have permission to change carrier handoff defaults."
        : "Default handoff could not be saved. Your previous selection was restored.";
      setSelected(previousValue);
      setError(message);
      toast.error(message);
    } finally {
      mutationLock.current = false;
      setPending(false);
    }
  };

  const selectedLocation = cell.dropoffLocations.find(
    (location) => location.id === selected
  );

  return (
    <div
      className="grid gap-3 p-3 md:grid-cols-[minmax(10rem,0.7fr)_minmax(16rem,1.3fr)] md:items-center"
      aria-busy={pending || undefined}
    >
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">
          {cell.labelCarrier.name}
        </div>
        <div className="font-mono text-xs text-muted-foreground">
          {cell.labelCarrier.code}
        </div>
      </div>

      {cell.dropoffLocations.length === 0 ? (
        <p
          role="status"
          className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800"
        >
          <TriangleAlert className="h-4 w-4 shrink-0" />
          No dropoff locations. Ask EZX to add {cell.labelCarrier.name}{" "}
          locations.
        </p>
      ) : (
        <div className="space-y-1">
          <NativeSelect
            aria-label={`Default dropoff for ${cell.labelCarrier.name}`}
            value={selected}
            disabled={pending}
            onChange={(event) => void handleChange(event.target.value)}
          >
            <option value="">No default — manual assignment</option>
            {cell.dropoffLocations.map((location) => (
              <option key={location.id} value={location.id}>
                {locationOption(location)}
              </option>
            ))}
          </NativeSelect>
          <p className="text-xs text-muted-foreground">
            {selectedLocation
              ? locationAddress(selectedLocation)
              : "Drivers choose the handoff location during assignment."}
          </p>
          {pending && (
            <p role="status" className="text-xs text-muted-foreground">
              Saving handoff default…
            </p>
          )}
          {error && (
            <p role="alert" className="text-xs text-destructive">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function SectionHeading({
  id,
  icon,
  title,
  description,
}: {
  id: string;
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div>
      <h2 id={id} className="flex items-center gap-2 text-base font-semibold">
        {icon}
        {title}
      </h2>
      <p className="mt-1 max-w-3xl text-xs text-muted-foreground">
        {description}
      </p>
    </div>
  );
}

function SectionLoading({ label }: { label: string }) {
  return (
    <div role="status" aria-label={label} className="space-y-2">
      <Skeleton className="h-28" />
      <Skeleton className="h-28" />
    </div>
  );
}

function SectionError({
  title,
  message,
  onRetry,
}: {
  title: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <Card>
      <CardContent role="alert" className="p-4">
        <div className="flex items-start gap-3">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold">{title}</h3>
            <p className="mt-1 text-xs text-muted-foreground">{message}</p>
          </div>
          {onRetry && (
            <Button
              variant="outline"
              size="touch"
              className="shrink-0"
              onClick={onRetry}
            >
              Try again
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function CachedDataWarning({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div
      role="alert"
      className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 sm:flex-row sm:items-center sm:justify-between"
    >
      <span>{message}</span>
      <Button
        variant="outline"
        size="touch"
        className="bg-background"
        onClick={onRetry}
      >
        Try again
      </Button>
    </div>
  );
}

function EmptySection({ title, message }: { title: string; message: string }) {
  return (
    <Card>
      <CardContent role="status" className="p-6 text-center">
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="mt-1 text-xs text-muted-foreground">{message}</p>
      </CardContent>
    </Card>
  );
}

function groupServingCells(cells: ServingCell[]) {
  const byDispatch = new Map<
    string,
    { name: string; code: string; cells: ServingCell[] }
  >();
  for (const cell of cells) {
    const existing = byDispatch.get(cell.dispatchCenterId);
    if (existing) {
      existing.cells.push(cell);
    } else {
      byDispatch.set(cell.dispatchCenterId, {
        name: cell.dispatchCenterName,
        code: cell.dispatchCenterCode,
        cells: [cell],
      });
    }
  }
  return [...byDispatch.entries()];
}

function locationOption(location: ServingCell["dropoffLocations"][number]) {
  const address = locationAddress(location);
  return address ? `${location.name} — ${address}` : location.name;
}

function locationAddress(
  location: ServingCell["dropoffLocations"][number]
): string {
  if (!location.address) return "Address unavailable";
  return [
    location.address.line1,
    location.address.city,
    location.address.province,
  ]
    .filter(Boolean)
    .join(", ");
}
